import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { buildLdSurveySubmitBodyFromStorage, submitCustomerSurveyTng } from '@/api/customerSurvey';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { clearAllLocalStorage } from '@/lib/clearAllLocalStorage';
import { isLdProductNowComplete, readLdProductNowAnswers } from '@/lib/ldProductNowStorage';
import { isOtherPrevBrandAnswerComplete } from '@/lib/otherPrevBrandAnswerStorage';
import { isOtherPrevBrandComplete } from '@/lib/otherPrevBrandStorage';
import { isPrevBrandAnswerComplete, readPrevBrandAnswerAnswers } from '@/lib/prevBrandAnswerStorage';
import { isPrevBrandComplete } from '@/lib/prevBrandStorage';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { writeTngRewardSession } from '@/lib/tngRewardSession';
import {
  readSetBLdBrandOpinionAnswers,
  SET_B_LD_FUTURE_LIKELIHOOD_OPTIONS,
  writeSetBLdBrandOpinionAnswers,
  type SetBLdBrandOpinionAnswers,
} from '@/lib/setBLdBrandOpinionStorage';
import { readWhyNotLdAnswers, writeWhyNotLdAnswers } from '@/lib/whyNotLdStorage';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const textareaClass =
  'min-h-[140px] min-w-0 w-full resize-y rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] leading-tight text-slate-900 outline-none placeholder:text-slate-700 focus:border-slate-600 sm:min-h-[160px] sm:text-[16px]';

const inputClass =
  'min-w-0 w-full rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

export default function WhyNotLd() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet, mobileLocalDigits } = useAppSelector(selectOtpDraft);

  const [answer, setAnswer] = useState(() => readWhyNotLdAnswers().answer);
  const [brandOpinionAnswers, setBrandOpinionAnswers] = useState<SetBLdBrandOpinionAnswers>(() =>
    readSetBLdBrandOpinionAnswers()
  );
  const [warning, setWarning] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSetBOtherProductPath = questionnaireSet === 'B';

  useEffect(() => {
    writeWhyNotLdAnswers({ answer });
  }, [answer]);

  useEffect(() => {
    if (!isSetBOtherProductPath) return;
    writeSetBLdBrandOpinionAnswers(brandOpinionAnswers);
  }, [brandOpinionAnswers, isSetBOtherProductPath]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;

  if (questionnaireSet === 'B') {
    if (!isLdProductNowComplete()) return <Navigate to="/ldproductnow" replace />;
    const smokedOtherProduct =
      readPrevBrandAnswerAnswers().smokedOtherProduct || readLdProductNowAnswers().smokedOtherProduct;
    if (smokedOtherProduct === 'yes') {
      if (!isOtherPrevBrandComplete()) return <Navigate to="/survey/prevbrand-other" replace />;
      if (!isOtherPrevBrandAnswerComplete()) {
        return <Navigate to="/survey/prevbrandanswer-other" replace />;
      }
    } else if (smokedOtherProduct === 'no') {
      return <Navigate to="/survey/set-b/brand-opinion" replace />;
    } else {
      return <Navigate to="/ldproductnow" replace />;
    }
  } else {
    if (!isPrevBrandComplete()) return <Navigate to="/survey/prevbrand" replace />;
    if (!isPrevBrandAnswerComplete()) return <Navigate to="/survey/prevbrandanswer" replace />;

    const smokedOtherProduct = readPrevBrandAnswerAnswers().smokedOtherProduct;
    if (smokedOtherProduct === 'yes') {
      if (!isOtherPrevBrandComplete()) return <Navigate to="/survey/prevbrand-other" replace />;
      if (!isOtherPrevBrandAnswerComplete()) {
        return <Navigate to="/survey/prevbrandanswer-other" replace />;
      }
    } else if (smokedOtherProduct !== 'no') {
      return <Navigate to="/survey/prevbrandanswer" replace />;
    }
  }

  const handleSubmit = () => {
    if (!isSetBOtherProductPath) {
      const trimmed = answer.trim();
      if (!trimmed) {
        setWarning(t.whyNotLd.answerWarning);
        return;
      }
      writeWhyNotLdAnswers({ answer: trimmed });
    } else {
      const brandOpinion = brandOpinionAnswers.brandOpinion.trim();
      const futureLikelihoodWhy = brandOpinionAnswers.futureLikelihoodWhy.trim();

      if (!brandOpinion) {
        setWarning(t.setBLdBrandOpinion.brandOpinionWarning);
        return;
      }
      if (!brandOpinionAnswers.futureLikelihood) {
        setWarning(t.setBLdBrandOpinion.likelihoodWarning);
        return;
      }
      if (!futureLikelihoodWhy) {
        setWarning(t.setBLdBrandOpinion.likelihoodWhyWarning);
        return;
      }

      writeSetBLdBrandOpinionAnswers({
        brandOpinion,
        futureLikelihood: brandOpinionAnswers.futureLikelihood,
        futureLikelihoodWhy,
      });
    }

    if (!customerUuid) {
      setWarning(t.whyNotLd.sessionExpiredWarning);
      return;
    }

    const body = buildLdSurveySubmitBodyFromStorage(mobileLocalDigits);
    if (!body) {
      setWarning(t.whyNotLd.incompleteWarning);
      return;
    }

    setSubmitting(true);
    setWarning(null);
    void submitCustomerSurveyTng(customerUuid, body)
      .then((result) => {
        writeTngRewardSession({
          pin: result.pin,
          expirydate: result.expirydate,
          value: result.value,
        });
        clearAllLocalStorage();
        navigate('/tng', { replace: true });
      })
      .catch((err: unknown) => {
        setWarning(err instanceof Error ? err.message : t.whyNotLd.submitError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3 pb-4">
              {!isSetBOtherProductPath ? (
                <SurveyFormPanel className="flex flex-col gap-2">
                  <p className={surveyQuestionTextClass}>{t.whyNotLd.question}</p>
                  <textarea
                    id="why-not-ld-answer"
                    value={answer}
                    onChange={(e) => {
                      setAnswer(e.target.value);
                      setWarning(null);
                    }}
                    placeholder={t.whyNotLd.placeholder}
                    className={textareaClass}
                  />
                </SurveyFormPanel>
              ) : null}

              {isSetBOtherProductPath ? (
                <>
                  <SurveyFormPanel className="flex flex-col gap-2">
                    <p className={surveyQuestionTextClass}>{t.setBLdBrandOpinion.brandOpinionQuestion}</p>
                    <textarea
                      id="set-b-ld-brand-opinion"
                      value={brandOpinionAnswers.brandOpinion}
                      onChange={(e) => {
                        setBrandOpinionAnswers((prev) => ({ ...prev, brandOpinion: e.target.value }));
                        setWarning(null);
                      }}
                      placeholder={t.setBLdBrandOpinion.brandOpinionPlaceholder}
                      className={textareaClass}
                    />
                  </SurveyFormPanel>

                  <SurveyFormPanel className="flex flex-col gap-2">
                    <p id="set-b-ld-future-likelihood-question" className={surveyQuestionTextClass}>
                      {t.setBLdBrandOpinion.futureLikelihoodQuestion}
                    </p>
                    <select
                      id="set-b-ld-future-likelihood"
                      value={brandOpinionAnswers.futureLikelihood}
                      onChange={(e) => {
                        const futureLikelihood =
                          e.target.value as SetBLdBrandOpinionAnswers['futureLikelihood'];
                        setBrandOpinionAnswers((prev) => ({ ...prev, futureLikelihood }));
                        setWarning(null);
                      }}
                      className={inputClass}
                      aria-labelledby="set-b-ld-future-likelihood-question"
                    >
                      <option value="" disabled>
                        {t.setBLdBrandOpinion.selectLikelihood}
                      </option>
                      {SET_B_LD_FUTURE_LIKELIHOOD_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>

                    <p className={surveyQuestionTextClass}>{t.setBLdBrandOpinion.whyQuestion}</p>
                    <textarea
                      id="set-b-ld-future-likelihood-why"
                      value={brandOpinionAnswers.futureLikelihoodWhy}
                      onChange={(e) => {
                        setBrandOpinionAnswers((prev) => ({
                          ...prev,
                          futureLikelihoodWhy: e.target.value,
                        }));
                        setWarning(null);
                      }}
                      className={textareaClass}
                    />
                  </SurveyFormPanel>
                </>
              ) : null}
            </div>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-4 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" disabled={submitting} onClick={handleSubmit}>
                {isSetBOtherProductPath ? t.setBLdBrandOpinion.submit : t.whyNotLd.submit}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
